// THE ZONE — the multiplayer lobby, named for the MSN Gaming Zone.
//
// One game so far: MINESWEEPER FLAGS, the real two-player Microsoft variant. 16x16, 51 mines,
// first to 26. Find a mine and you score it and go again; find empty ground and the turn passes.
//
// Free play. No wallet, no chips, no money — proving multiplayer with nothing at risk is the
// point of shipping this before the wagered version.
//
// The board lives on the server and never crosses the wire, so nothing here can read the mines
// out of its own state. This component renders exactly what it is told.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CHAT_HOST } from '../Messenger';
import { AddressBar, Counter, Marquee, Mast, StatusBar, XP, webCss, xpCss } from 'lib/xp';
import pfp from 'assets/pfp.png';

// The Zone shares the messenger's worker. `window.__IC_ZONE_HOST__` exists so a local
// `wrangler dev` can be pointed at during verification without touching production; nothing in
// the app ever sets it, and setting it only redirects the setter's own socket.
function zoneUrl() {
  const override = typeof window !== 'undefined' && window.__IC_ZONE_HOST__;
  if (override) {
    const scheme = /^(localhost|127\.)/.test(override) ? 'ws' : 'wss';
    return `${scheme}://${override}/zone/ws`;
  }
  return `wss://${CHAT_HOST}/zone/ws`;
}
const RECONNECT_MS = 2500;

const nickKey = 'ic-zone-nick';
const savedNick = () => {
  try {
    return window.localStorage.getItem(nickKey) || '';
  } catch (e) {
    return '';
  }
};

export default function Zone() {
  const [nick, setNick] = useState(savedNick);
  const [status, setStatus] = useState('connecting');
  const [zone, setZone] = useState({ online: 0, waiting: 0 });
  const [state, setState] = useState(null);
  const [queued, setQueued] = useState(false);
  const [note, setNote] = useState('');
  const [now, setNow] = useState(Date.now());
  const ws = useRef(null);
  const retry = useRef(null);

  const send = useCallback(frame => {
    const s = ws.current;
    if (s && s.readyState === 1) s.send(JSON.stringify(frame));
  }, []);

  useEffect(() => {
    let dead = false;

    function open() {
      if (dead) return;
      const socket = new WebSocket(zoneUrl());
      ws.current = socket;
      setStatus('connecting');

      socket.onopen = () => {
        setStatus('online');
        socket.send(JSON.stringify({ t: 'hello', nick: savedNick() }));
      };
      socket.onmessage = e => {
        let f;
        try {
          f = JSON.parse(e.data);
        } catch (err) {
          return;
        }
        if (f.t === 'welcome') {
          setNick(f.nick);
          try {
            window.localStorage.setItem(nickKey, f.nick);
          } catch (err) {
            /* private window */
          }
        }
        if (f.t === 'zone') setZone({ online: f.online, waiting: f.waiting });
        if (f.t === 'queued') {
          setQueued(true);
          setNote('waiting for a fren…');
        }
        if (f.t === 'unqueued') {
          setQueued(false);
          setNote('');
        }
        if (f.t === 'match' || f.t === 'state') {
          setQueued(false);
          setState(f.s);
          setNote('');
        }
        if (f.t === 'timeout') setNote('their clock ran out — your turn');
        if (f.t === 'error') setNote(String(f.why || ''));
      };
      socket.onclose = () => {
        if (dead) return;
        setStatus('offline');
        retry.current = setTimeout(open, RECONNECT_MS);
      };
      socket.onerror = () => socket.close();
    }

    open();
    const clock = setInterval(() => setNow(Date.now()), 500);
    return () => {
      dead = true;
      clearInterval(clock);
      if (retry.current) clearTimeout(retry.current);
      if (ws.current) ws.current.close();
    };
  }, []);

  const play = () => {
    send({ t: 'hello', nick });
    send({ t: 'queue' });
  };

  const inMatch = state && !state.over;
  const myTurn = inMatch && state.turn === state.you;
  const secondsLeft = inMatch ? Math.max(0, Math.round((state.turnEndsAt - now) / 1000)) : 0;

  const statusText = status === 'online' ? `${zone.online} online · ${zone.waiting} waiting` : status;

  return (
    <Div>
      <div className="xp__menubar">
        <button type="button">Game</button>
        <button type="button">Zone</button>
        <button type="button">Help</button>
        <span className="xp__menubar__brand">The Zone</span>
      </div>
      <AddressBar path={state ? 'zone.internetcoin.vip/rooms/minesweeperflags/table.htm' : 'zone.internetcoin.vip/rooms/minesweeperflags.htm'} />

      <div className="xp__client web web--teal">
        <Mast
          logo={pfp}
          kicker="the internet's gaming zone"
          title="THE ZONE"
          right={
            <>
              <span className="web__sticker web__sticker--blue">FREE PLAY</span>
              <span className="zn__visitor">
                players online <Counter value={zone.online} digits={3} />
              </span>
            </>
          }
        />
        <Marquee>
          ★ MINESWEEPER FLAGS ★ the two-player one ★ 16×16 · 51 mines · first to 26 ★ free play · no wallet · no chips · nothing at stake ★ {statusText} ★ find a mine and go again ★
        </Marquee>

        <div className="web__body">
      <div className="zn__bar">
        <span className="zn__logo">minesweeper flags</span>
        <span className={`zn__status zn__status--${status}`}>{statusText}</span>
      </div>

      {!state ? (
        <div className="zn__lobby web__cols">
          <div className="web__panel web__panel--teal zn__room">
            <div className="web__panel__t">Minesweeper Flags · room 1</div>
            <div className="zn__pitch">
              <b>MINESWEEPER FLAGS</b> — the two-player one. 16×16, 51 mines, first to 26 wins. Find a
              mine and you take it <i>and go again</i>. Find empty ground and it&apos;s their turn.
            </div>
            <div className="zn__row">
              <label htmlFor="zn-nick">Name</label>
              <input
                id="zn-nick"
                className="xp__field"
                value={nick}
                maxLength={20}
                onChange={e => setNick(e.target.value)}
                placeholder="fren"
              />
            </div>
            <div className="zn__actions">
              <button
                type="button"
                className={`web__btn web__btn--big zn__play${status !== 'online' || queued ? ' web__btn--off' : ''}`}
                disabled={status !== 'online' || queued}
                onClick={queued ? () => send({ t: 'unqueue' }) : play}
              >
                {queued ? 'WAITING FOR A FREN…' : 'PLAY'}
              </button>
              {queued ? (
                <button type="button" className="web__btn web__btn--red zn__cancel" onClick={() => send({ t: 'unqueue' })}>
                  cancel
                </button>
              ) : null}
            </div>
            <div className="zn__free">free play · no wallet · no chips · nothing at stake</div>
          </div>
          <div className="web__panel web__panel--navy zn__side__panel">
            <div className="web__panel__t">How it works</div>
            <div className="web__step">
              <span className="web__step__n">1</span>
              <div><b>Pick a name, press PLAY.</b> You wait in the room until a fren shows up. First come, first paired.</div>
            </div>
            <div className="web__step">
              <span className="web__step__n">2</span>
              <div><b>Click squares.</b> A mine is a point and another go. Empty ground passes the turn. 20 seconds a move.</div>
            </div>
            <div className="web__step">
              <span className="web__step__n">3</span>
              <div><b>26 mines wins.</b> The board lives on the server; nothing on your side knows where the mines are.</div>
            </div>
            <div className="web__note">The wagered tables are in CASINO.exe. This room is free, forever.</div>
          </div>
        </div>
      ) : (
        <div className="zn__game__wrap">
          <div className="zn__score">
            <div className={`zn__side web__panel web__panel--red${state.you === 0 ? ' me' : ''}${state.turn === 0 && !state.over ? ' on' : ''}`}>
              <span className="n">{state.nicks[0]}</span>
              <span className="v web__led">{state.scores[0]}</span>
            </div>
            <div className="zn__mid">
              {state.over
                ? state.winner === state.you
                  ? 'YOU WIN'
                  : 'YOU LOSE'
                : myTurn
                ? `YOUR TURN · ${secondsLeft}s`
                : 'their turn'}
            </div>
            <div className={`zn__side web__panel web__panel--blue${state.you === 1 ? ' me' : ''}${state.turn === 1 && !state.over ? ' on' : ''}`}>
              <span className="v web__led">{state.scores[1]}</span>
              <span className="n">{state.nicks[1]}</span>
            </div>
          </div>

          <div className="zn__board" style={{ gridTemplateColumns: `repeat(${state.w}, 1fr)` }}>
            {Array.from({ length: state.w * state.h }).map((_, c) => {
              const ground = state.ground[c];
              const flag = state.flags[c];
              const isFlag = flag !== undefined;
              const isGround = ground !== undefined;
              const cls = [
                'zn__cell',
                isFlag ? `flag flag--${flag}` : '',
                isGround ? 'open' : '',
                isGround && ground > 0 ? `n${ground}` : '',
                !isFlag && !isGround && myTurn ? 'live' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <button
                  key={c}
                  type="button"
                  className={cls}
                  disabled={!myTurn || isFlag || isGround || state.over}
                  onClick={() => send({ t: 'click', cell: c })}
                >
                  {isFlag ? '⚑' : isGround && ground > 0 ? ground : ''}
                </button>
              );
            })}
          </div>

          <div className="zn__foot">
            {note ? <span className="zn__note">{note}</span> : null}
            {state.over ? (
              <button
                type="button"
                className="web__btn zn__again"
                onClick={() => {
                  setState(null);
                  setNote('');
                }}
              >
                BACK TO THE ZONE
              </button>
            ) : (
              <button type="button" className="web__btn web__btn--red zn__resign" onClick={() => send({ t: 'resign' })}>
                resign
              </button>
            )}
          </div>
        </div>
      )}
          <div className="web__foot">Best viewed in Internet Explorer 6 at 800×600 · © 2001 The Zone · free play, nothing at stake</div>
        </div>
      </div>

      <StatusBar>
        <span>{statusText}</span>
        <span>free play</span>
        <span>Internet</span>
      </StatusBar>
    </Div>
  );
}

const Div = styled.div`
  ${xpCss};
  ${webCss};
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  -webkit-font-smoothing: antialiased;

  .xp__client {
    flex: 1;
    overflow: auto;
    padding: 0;
  }
  .zn__visitor {
    font-size: 10px;
    color: #fff3dc;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .zn__bar {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 8px;
  }
  .zn__logo {
    font: bold 14px ${'\'Times New Roman\', Times, serif'};
    letter-spacing: 2px;
    text-transform: uppercase;
    color: ${XP.ink};
  }
  .zn__status {
    margin-left: auto;
    font-size: 11px;
    color: #4a4a4a;
  }
  .zn__status--online {
    color: #1e8b3a;
    font-weight: bold;
  }
  .zn__status--offline {
    color: ${XP.red};
    font-weight: bold;
  }

  .zn__lobby.web__cols {
    align-items: flex-start;
  }
  .zn__room {
    flex: 1.35;
  }
  .zn__pitch {
    font-size: 11px;
    line-height: 1.6;
    color: #1d1d1d;
    margin-bottom: 12px;
  }
  .zn__pitch b {
    color: #000;
  }
  .zn__row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }
  .zn__row label {
    width: 44px;
    font-weight: bold;
  }
  .zn__row input {
    flex: 1;
    min-width: 0;
    height: 21px;
  }
  .zn__actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .zn__play {
    flex: 1;
    text-align: center;
  }
  .zn__cancel {
    font-size: 11px;
    padding: 6px 12px;
  }
  .zn__free {
    margin-top: 10px;
    font-size: 10px;
    color: #4a3600;
    text-align: center;
    letter-spacing: 1px;
  }
  .zn__side__panel {
    flex: 1;
  }
  @media (max-width: 520px) {
    .zn__lobby.web__cols {
      flex-direction: column;
    }
  }

  .zn__game__wrap {
    padding-bottom: 6px;
  }
  .zn__score {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
  }
  .zn__side {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    margin: 0;
    min-width: 96px;
  }
  .zn__side.on {
    box-shadow: 0 0 0 2px ${XP.lime}, 2px 2px 0 rgba(0, 0, 0, 0.28);
  }
  .zn__side .n {
    font-size: 11px;
    color: #1d1d1d;
    max-width: 74px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .zn__side.me .n {
    font-weight: bold;
  }
  .zn__side .v {
    font-size: 14px;
    padding: 2px 8px;
  }
  .zn__mid {
    font: bold 12px ${'\'Times New Roman\', Times, serif'};
    letter-spacing: 1px;
    color: #000;
    text-align: center;
    flex: 1;
  }

  /* the board is the real thing: raised grey tiles, flat open ground, the classic number colours */
  .zn__board {
    display: grid;
    gap: 0;
    background: #c0c0c0;
    border: 3px solid #808080;
    border-right-color: #fff;
    border-bottom-color: #fff;
  }
  .zn__cell {
    aspect-ratio: 1;
    box-sizing: border-box;
    background: #c0c0c0;
    border: 2px solid #f5f5f5;
    border-right-color: #7b7b7b;
    border-bottom-color: #7b7b7b;
    color: #000;
    font: bold 11px ${'Tahoma'}, sans-serif;
    padding: 0;
    cursor: default;
    min-width: 0;
    line-height: 1;
  }
  .zn__cell.live {
    cursor: pointer;
  }
  .zn__cell.live:hover {
    background: #d4d4d4;
  }
  .zn__cell.open {
    background: #c0c0c0;
    border: 1px solid #a8a8a8;
    border-top-color: #808080;
    border-left-color: #808080;
  }
  .zn__cell.n1 { color: #0000ff; }
  .zn__cell.n2 { color: #008000; }
  .zn__cell.n3 { color: #ff0000; }
  .zn__cell.n4 { color: #000080; }
  .zn__cell.n5 { color: #800000; }
  .zn__cell.n6 { color: #008080; }
  .zn__cell.n7 { color: #000000; }
  .zn__cell.n8 { color: #808080; }
  .zn__cell.flag {
    font-size: 12px;
    background: #d9d9d9;
    border: 1px solid #a8a8a8;
  }
  .zn__cell.flag--0 {
    color: ${XP.red};
  }
  .zn__cell.flag--1 {
    color: ${XP.blue};
  }

  .zn__foot {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  .zn__resign,
  .zn__again {
    font-size: 12px;
    padding: 5px 14px;
  }
  .zn__note {
    font-size: 11px;
    color: ${XP.red};
    font-weight: bold;
  }
`;
